import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { RootState } from '../../app/store';
import {
  fetchFlights,
  fetchHangar,
  fetchHangarControl,
  fetchCleosList,
  fetchStartCleosStream,
  fetchDroneState,
  fetchDroneControl,
  fetchDronePositionPull,
  fetchGeolocation,
  fetchCleosActiveStreams,
  fetchMavlinkControlGet,
  fetchMavlinkControlPost,
  fetchMavlinkInitialized,
  linkTask,
  unlinkTask,
  checkTask,
  linkJob,
  unlinkJob,
  checkJob,
  fetchSocketConnected,
  fetchDroneTracker,
  fetchDronesTracker,
  fetchDronesConflict,
  joinIconToJob,
  uploadIcon,
  changeCylinder,
  joinCylinderToTask,
  fetchIcons,
  fetchDronesNearTracker,
  fetchStopCleosStream,
  lastPosJob,
  fetchAllDetections,
  fetchColorDetectionsList
} from '../../services/monitoringAPI';
import { CleosStatusI, MonitoringDrone, MonitoringDroneAll,
  MonitoringSliceProps } from '../../model/realtimeMonitoringInterface';
import { openToast } from '../../utility/toast';
import { GenericTasksState } from '../../model/tasksInterface';
import { AddWarning } from '../../services/warningAPI';
import { isCommandWarning } from './utils';

const initialState: MonitoringSliceProps = {
  flights: {
    data: null,
    status: 'idle'
  },
  detail: false,
  droneDetail: {
    data: null,
    status: 'idle'
  },
  dronePopup: {
    data: null,
    status: 'idle'
  },
  drone: {
    data: null,
    status: 'idle'
  },
  drones: {
    data: [],
    status: 'idle'
  },
  dronesAll: {
    data: [],
    status: 'idle'
  },
  dronesConflict: {
    data: [],
    status: 'idle'
  },
  hangar: {
    data: null,
    status: 'idle'
  },
  hangarControl: {
    data: null,
    status: 'idle'
  },
  cleosList: {
    data: null,
    status: 'idle'
  },
  colorDetectionsList: {
    data: null,
    status: 'idle'
  },
  cleosStatus: {
    data: undefined,
    status: 'idle'
  },
  cleosTimer: {},
  c2Timer: {},
  startCleosStream: {
    data: null,
    status: 'idle'
  },
  droneState: {
    data: null,
    status: 'idle'
  },
  droneControl: {
    data: null,
    status: 'idle'
  },
  trackingMode: '',
  cleosActiveStreams: {
    data: null,
    status: 'idle'
  },
  mavlink: {
    data: null,
    status: 'idle'
  },
  mavlinkLoadMission: {
    data: null,
    status: 'idle'
  },
  detectionGeoLocation: {
    which_detection: [],
  },
  detectionGeoLocationFetch: {
    data: [],
    status: 'idle'
  },
  mavLinkInitialized: {
    data: false,
    status: 'idle'
  },
  guided: '',
  geoState: {},
  detectionActionState: {},
  guidedFlag: false,
  socketInitialized: false,
  socketConnected: {
    data: false,
    status: 'idle'
  },
  value: 0,
  zoom: 7,
  featuresClick: null,
  inputBuffer: {
    data: {
      distance: 1,
      distanceV: 1,
      height: 15
    },
    status: 'idle'
  },
  linkTaskData: {
    data: null,
    status: 'idle'
  },
  unlinkTaskData: {
    data: null,
    status: 'idle'
  },
  checkTaskData: {
    data: null,
    status: 'idle'
  },
  linkJobData: {
    data: null,
    status: 'idle'
  },
  unlinkJobData: {
    data: null,
    status: 'idle'
  },
  checkJobData: {
    data: null,
    status: 'idle'
  },
  selected: false,
  automaticActionEnabled: false,
  changeIconState: {
    status: 'idle'
  },
  changeCylinderState: {
    status: 'idle'
  },
  popupView: {},
  icons: {
    data: null,
    status: 'idle'
  },
  cleosRunId: undefined
};

export const getDroneStateAsync = createAsyncThunk(
  'monitoring/fetchDroneState',
  async ({id, hangarIdentifier}: any) => {
    try {
      const response: any = await fetchDroneState({ id, hangarIdentifier });
      return response.data;
    } catch (e: any) {
      return Promise.reject(e.data ? e.data : e);
    }
  }
);

export const postDroneControlAsync = createAsyncThunk(
  'monitoring/fetchDroneControl',
  async ({ cmd, uav_id, ...rest }: any) => {
    try {
      console.log('postDroneControlAsync', rest);
      const response: any = await fetchDroneControl({ cmd, uav_id, ...rest });
      const severity = response?.data?.status === 'error' ? 'error' : 'success';
      const alertMessage = severity === 'error'?'FetchKO':'FetchOK';
      openToast({
        severity: severity,
        alertMessage: alertMessage
      });


      if (isCommandWarning(cmd)) {
        await AddWarning({
          ...rest,
          type: 'command',
          severity: severity,
          info: {
            status: response?.data?.status === 'error' ? 'nok' : 'ok',
            event: { response:  alertMessage , type: cmd,
              hangarIdentifier: rest?.hangarIdentifier}
          }
        });

      }

      return response?.data;
    } catch (e: any) {
      openToast({
        severity: 'error',
        alertMessage: 'FetchKO'
      });
      if (isCommandWarning(cmd)) {
        await AddWarning({
          ...rest,
          type: 'command',
          severity: 'error',
          info: {
            status: 'nok',
            event: {
              response: 'FetchKO', type: cmd,
              hangarIdentifier: rest?.hangarIdentifier
            }
          }
        });

      }
      return Promise.reject(e.data ? e.data : e);
    }
  }
);

export const getMavlinkControlAsync = createAsyncThunk(
  'monitoring/fetchMavlinkControlGet',
  async ({ cmd, uav_id,   ...rest }: any) => {
    try {
      const response: any = await fetchMavlinkControlGet({
        cmd,
        uav_id,
        ...rest
      });

      const alertMessage = response?.data?.status ??
        (response && response?.data && response?.data?.status !== 'error' ? 'FetchOK' : 'FetchKO');
      const severity =
        response &&
        response?.data &&
        response?.data?.status === 'error' ? 'error' : 'success';

      openToast({
        severity: severity,
        alertMessage: alertMessage
      });

      if (isCommandWarning(cmd)) {
        await AddWarning({
          ...rest,
          type: 'command',
          severity: severity,
          info: {
            status: response && response?.data && response?.data?.status === 'error' ? 'nok' : 'ok',
            event: { response:  alertMessage , type: cmd }
          }
        });

      }

      return response?.data;
    } catch (e: any) {
      const alertMessage = e?.data?.status ?? 'FetchKO';
      openToast({
        severity: 'error',
        alertMessage: alertMessage
      });
      if (isCommandWarning(cmd)) {
        await AddWarning({
          ...rest,
          type: 'command',
          severity: 'error',
          info: {
            status:   'nok' ,
            event: { response:  alertMessage , type: cmd }
          }
        });

      }
      return Promise.reject(e.data ? e.data : e);
    }
  }
);

export const initializeMAVLinkAsync = createAsyncThunk(
  'monitoring/fetchMavlinkControlGet',
  async ({ cmd, uav_id, port, ...rest }: any) => {
    try {
      const response: any = await fetchMavlinkControlGet({
        cmd,
        uav_id,
        port,
        ...rest
      });
      if (response?.data?.status === 'error') {
        openToast({
          severity: 'error',
          alertMessage: response?.data?.status ?? 'FetchKO'
        });
      }
      return response?.data;
    } catch (e: any) {
      openToast({
        severity: 'error',
        alertMessage: e?.data?.status ?? 'FetchKO'
      });
      return Promise.reject(e.data ? e.data : e);
    }
  }
);

export const postMavlinkControlAsync = createAsyncThunk(
  'monitoring/fetchMavlinkControlPost',
  async ({ cmd, uav_id, offset,   ...rest }: any) => {
    try {
      const response: any = await fetchMavlinkControlPost({
        cmd,
        uav_id,
        offset,
        ...rest
      });
      openToast({
        severity: 'success',
        alertMessage: response?.data?.status ?? 'FetchOK'
      });
      return response?.data;
    } catch (e: any) {
      openToast({
        severity: 'error',
        alertMessage: e?.data?.status ?? 'FetchKO'
      });
      return Promise.reject(e.data ? e.data : e);
    }
  }
);

export const getMavlinkInitializedAsync = createAsyncThunk(
  'monitoring/fetchMavlinkInitialized',
  async ({ uav_id  }: any) => {

    try {
      const response: any = await fetchMavlinkInitialized({ uav_id });
      return response.data;
    } catch (e: any) {
      return Promise.reject(e.data ? e.data : e);
    }
  }
);

export const getDronePositionAsync = createAsyncThunk(
  'monitoring/dronePosition',
  async ({
    uavId,
    type,
    jobId,
    ctx,
    hangarIdentifier
  }: {
    uavId: string;
    type: string;
    jobId: string;
    ctx: any;
    hangarIdentifier: string;
  }) => {
    try {

      const response: any = await
      fetchDronePositionInner(uavId, type, jobId, ctx, hangarIdentifier);
      return response;
    } catch (e: any) {
      return Promise.reject(e.data ? e.data : e);
    }
  }
);

export const fetchDronePositionInner = async (uavId: string,
  type: string,
  jobId: string,
  ctx: any,
  hangarIdentifier: string) => {
  let response: any;
  /*
  // if (  type === 'VIRTUAL BOX') {
  //     response =  await fetchDronePositionPull(uavId, hangarIdentifier);
  //   } else { 
  //     const typeM: string | undefined = (type === 'COM-BOX') ? 'default' :
  //       ((type === 'MAVLINK') ? 'mavlink' :
  //       ((type === 'HANGAR') ? 'hangar' :
  //         ((type === 'AUGMENTATION') ? 'gnss' :
  //           (type === 'MOBILE APP') ? 'mobile' : undefined)));
  //     console.log(type);
  //     console.log(typeM);
  //     if (typeM) {
  //       response = await fetchDroneTracker(uavId, 'default', jobId, ctx);
  //     }
  //   }
  */
  if (type == 'AUGMENTATION') {
    response = await fetchDroneTracker(uavId, 'gnss', jobId, ctx);
  } else {
    response = await fetchDroneTracker(uavId, 'default', jobId, ctx);
  }

  return response?.data;
};

export const getDronesPositionAsync = createAsyncThunk(
  'monitoring/dronesPosition',
  async ({
    ctx
  }: {
    ctx: any;
  }) => {
    try {
      const response: any = await fetchDronesTracker(ctx, true);

      return response.data;
    } catch (e: any) {
      return Promise.reject(e.data ? e.data : e);
    }
  }
);

export const getDronesPositionAsyncAll = createAsyncThunk(
  'monitoring/dronesPositionAll',
  async ({
    ctx,
    uavId,
    radius
  }: {
    ctx: any;
    uavId: any;
    radius: any;
  }) => {
    try {
      const dronesAll: MonitoringDroneAll[] = [];
      const response: any = await fetchDronesNearTracker(ctx, uavId,  radius);


      const drones: MonitoringDrone[] = response.data;
      if (drones && drones.length) {
        const responseFligths: any = await fetchFlights(ctx);
        const tasks: GenericTasksState[] = responseFligths.data;

        drones.map((monitoringDrone) => {
          const droneAll: MonitoringDroneAll =
            { monitoring: monitoringDrone, task: undefined };
          if (tasks && tasks.length) {
            const task: GenericTasksState[] =
              tasks.filter(it => it?.id === monitoringDrone?.taskId);
            if (task && task.length) {
              droneAll.task = task[0];
            }
          }
          dronesAll.push(droneAll);
        }
        );


      }

      //console.log(dronesAll);
      return dronesAll;
    } catch (e: any) {
      return Promise.reject(e.data ? e.data : e);
    }
  }
);

export const getIcons = createAsyncThunk(
  'monitoring/icons',
  async ({
    ctx
  }: {
    ctx: any;
  }) => {
    try {
      const response: any = await fetchIcons(ctx);

      return response.data;
    } catch (e: any) {
      return Promise.reject(e.data ? e.data : e);
    }
  }
);



export const getDronesConflictAsync = createAsyncThunk(
  'monitoring/dronesConflicts',
  async ({
    ctx
  }: {
    ctx: any;
  }) => {
    try {
      const response: any = await fetchDronesConflict(ctx);
      if (response?.data === '') {
        return [];
      }

      return response?.data;
    } catch (e: any) {
      return Promise.reject(e.data ? e.data : e);
    }
  }
);



export const getHangarAsync = createAsyncThunk(
  'monitoring/fetchHangar',
  async ( hanId: any) => {
    try {
      const response: any = await fetchHangar(hanId, '');
      return response.data;
    } catch (e: any) {
      return Promise.reject(e.data ? e.data : e);
    }
  }
);

export const getHangarByHIDAsync = createAsyncThunk(
  'monitoring/fetchHangarByID',
  async ( hangarIdentifier: any) => {
    try {
      const response: any = await fetchHangar(0, hangarIdentifier);
      return response.data;
    } catch (e: any) {
      return Promise.reject(e.data ? e.data : e);
    }
  }
);

export const closeStreamingModelsAsynk = createAsyncThunk('monitoring/fetchStopCleosStream',
  async ({ uavId, ctx_id, runId, host_name, detection_id }:
    {uavId: string, ctx_id: string, runId: string, host_name: string, detection_id: string}) => {
    try {
      const response: any = await
      fetchStopCleosStream(uavId, ctx_id, runId, host_name, detection_id);
      return response.data;
    } catch (e: any) {
      openToast({
        severity: 'error',
        alertMessage: 'FetchKO'
      });
      return Promise.reject(e.data ? e.data : e);
    }
  }
);

export const postHangarAsync = createAsyncThunk(
  'monitoring/fetchHangarControl',
  async ({ hangar, hanId, hangarIdentifier }: any) => {
    try {
      const response: any = await fetchHangarControl({ hangar, hanId, hangarIdentifier });
      if (response?.data?.status !== 'error') {
        openToast({
          severity: 'success',
          alertMessage: 'FetchOK'
        });
      } else {
        openToast({
          severity: 'error',
          alertMessage: 'FetchKO'
        });
      }
      return response?.data;
    } catch (e: any) {
      openToast({
        severity: 'error',
        alertMessage: 'FetchKO'
      });
      return Promise.reject(e.data ? e.data : e);
    }
  }
);

export const getFlightsAsync = createAsyncThunk(
  'monitoring/fetchFlights',
  async (ctx: string) => {
    try {
      const response: any = await fetchFlights(ctx);

      const activeTasks: any[] = response.data;




      return activeTasks;
    } catch (e: any) {
      return Promise.reject(e.data ? e.data : e);
    }
  }
);




export const changeIconToUavId = createAsyncThunk(
  'monitoring/postIconToUavId',
  async ({
    ctx, jobId, fileSelected
  }: {
    ctx: string,
    jobId: string,
    fileSelected: any
  }) => {
    try {
      let iconId = fileSelected?.iconSelected ? fileSelected?.iconSelected : undefined;
      if (!fileSelected?.iconSelected) {
        const response: any = await uploadIcon(ctx, fileSelected);
        //console.log(response);
        iconId = response.data;
      }

      //console.log(response);
      const response: any = await joinIconToJob(jobId, iconId);
      //console.log(response);
      //const response: any = { data: { }};
      return response.data;
    } catch (e: any) {
      return Promise.reject(e.data ? e.data : e);
    }
  }
);

export const changeCylinderToUavId = createAsyncThunk(
  'monitoring/postCylinderToUavId',
  async ({
    ctx, jobId, uavId, radius, height
  }: {
    ctx: string,
    jobId: string,
    uavId: string,
    radius: number,
    height: number
  }) => {
    try {
      let response: any = await changeCylinder(ctx, jobId, uavId, radius, height);
      response = await joinCylinderToTask(jobId, radius, height);

      return response.data;
    } catch (e: any) {
      return Promise.reject(e.data ? e.data : e);
    }
  }
);


export const getCleosListAsync = createAsyncThunk(
  'monitoring/cleosList',
  async (ctx: string) => {
    try {
      const response: any = await fetchCleosList(ctx);
      return response.data;
    } catch (e: any) {
      return Promise.reject(e.data ? e.data : e);
    }
  }
);

export const getColorDetectionsListAsync = createAsyncThunk(
  'monitoring/colorDetectionsList',
  async (ctx: string) => {
    try {
      const response: any = await fetchColorDetectionsList(ctx);
      return response.data;
    } catch (e: any) {
      return Promise.reject(e.data ? e.data : e);
    }
  }
);

export const getCleosStatusAsync = createAsyncThunk(
  'monitoring/cleosStatus',
  async ({ ctx_id,
    runId,
    uavId

  }: {
    ctx_id: any;
    runId: any;
    uavId: any;
  }) => {
    try {
      const tot_active_detections: any[] | undefined = await fetchAllDetections({ctx_id:ctx_id});
      if (!tot_active_detections)
        return undefined;
      const myDetections : CleosStatusI[] = tot_active_detections
        .filter((it: any) =>
          it.user === localStorage.getItem('username')
          && it.run_id === runId && it.uav_id === uavId);
      return myDetections?.length>0?myDetections[0]:undefined;
    } catch (e: any) {
      return Promise.reject(e.data ? e.data : e);
    }
  }
);




export const getCleosActiveStreamsAsync = createAsyncThunk(
  'monitoring/cleosActiveStreams',
  async () => {
    try {
      const response: any = await fetchCleosActiveStreams();
      return response.data;
    } catch (e: any) {
      return Promise.reject(e.data ? e.data : e);
    }
  }
);

export const startCleosStreamAsync = createAsyncThunk(
  'monitoring/startCleosStream',
  async ({
    uav_id,
    video_url,
    which_detection,
    model,
    runId,
    username,
    ctx_id,
    detection_id,
    selection,
    setSelection
  }: {
    uav_id: string;
    video_url: string;
    which_detection: string[];
    model: string;
    runId: string;
    username: string;
    ctx_id: string;
    detection_id: string;
    selection: any;
    setSelection: any;
  }) => {
    try {
      const response: any = await fetchStartCleosStream({
        uav_id,
        video_url,
        which_detection,
        model,
        runId,
        username,
        ctx_id,
        detection_id
      });

      if (response?.data?.detection_id)
        setSelection(response?.data?.detection_id);

      return response?.data;
    } catch (e: any) {
      return Promise.reject(e.data ? e.data : e);
    }
  }
);

export const getDetectionGeolocationAsync = createAsyncThunk(
  'monitoring/detectionGeolocation',
  async ({
    uav_id,
    which_detection,
    detection_id
  }: {
    uav_id: string;
    which_detection: string[];
    detection_id: string;
  }) => {
    try {
      const response: any = await fetchGeolocation({
        uav_id,
        which_detection,
        detection_id
      });
      return  response.data;
    } catch (e: any) {
      return Promise.reject(e.data ? e.data : e);
    }
  }
);
/*
export const getCrowdGeolocationAsync = createAsyncThunk(
  'monitoring/crowdGeolocation',
  async ({
    uav_id,
    which_detection
  }: {
    uav_id: string;
    which_detection: string;
  }) => {
    try {
      const response: any = await fetchGeolocation({
        uav_id,
        which_detection
      });
      return response.data;
    } catch (e: any) {
      return Promise.reject(e.data ? e.data : e);
    }
  }
);

export const getFacemaskGeolocationAsync = createAsyncThunk(
  'monitoring/facemaskGeolocation',
  async ({
    uav_id,
    which_detection
  }: {
    uav_id: string;
    which_detection: string;
  }) => {
    try {
      const response: any = await fetchGeolocation({
        uav_id,
        which_detection
      });
      return response.data;
    } catch (e: any) {
      return Promise.reject(e.data ? e.data : e);
    }
  }
);

export const getWeaponGeolocationAsync = createAsyncThunk(
  'monitoring/weaponGeolocation',
  async ({
    uav_id,
    which_detection
  }: {
    uav_id: string;
    which_detection: string;
  }) => {
    try {
      const response: any = await fetchGeolocation({
        uav_id,
        which_detection
      });
      return response.data;
    } catch (e: any) {
      return Promise.reject(e.data ? e.data : e);
    }
  }
);

export const getPeopleGeolocationAsync = createAsyncThunk(
  'monitoring/peopleGeolocation',
  async ({
    uav_id,
    which_detection
  }: {
    uav_id: string;
    which_detection: string;
  }) => {
    try {
      const response: any = await fetchGeolocation({
        uav_id,
        which_detection
      });
      return response.data;
    } catch (e: any) {
      return Promise.reject(e.data ? e.data : e);
    }
  }
);*/

export const linkTaskAsync = createAsyncThunk(
  'monitoring/linkTask',
  async ({
    ref,
    task_id,
    ctx,
    task_label,
    job_id,
    radius,
    height,
  }: {
    ref: any;
    task_id: any;
    ctx: any;
    task_label: string;
    job_id: any;
    radius: any;
    height: any;
  }) => {
    try {
      const response: any = await linkTask(ref, task_id, ctx, task_label,job_id, radius, height);
      return response.data;
    } catch (e: any) {
      openToast({
        severity: 'error',
        alertMessage: e?.data?.details ?? 'FetchKO'
      });
      return Promise.reject(e.data ? e.data : e);
    }
  }
);

export const unlinkTaskAsync = createAsyncThunk(
  'monitoring/unlinkTask',
  async ({ ref, task_id, ctx }: { ref: any; task_id: any; ctx: any }) => {
    try {
      const response: any = await unlinkTask(ref, task_id, ctx);
      return response.data;
    } catch (e: any) {
      openToast({
        severity: 'error',
        alertMessage: e?.data?.details ?? 'FetchKO'
      });
      return Promise.reject(e.data ? e.data : e);
    }
  }
);

export const checkTaskAsync = createAsyncThunk(
  'monitoring/checkTask',
  async ({ ref, task_id, ctx, jobId }: { ref: any; task_id: any; ctx: any; jobId: any }) => {
    try {
      const response: any = await checkTask(ref, task_id, ctx, jobId);
      return response.data;
    } catch (e: any) {
      return Promise.reject(e.data ? e.data : e);
    }
  }
);

export const getLastPosJobAsync = createAsyncThunk(
  'monitoring/lastPosJob',
  async ({job_id, ctx}: {job_id: any; ctx: any;}) => {
    try {
      const response: any = await lastPosJob(job_id, ctx);

      return response;
    }
    catch (e: any) {
      openToast({
        severity: 'error',
        alertMessage: e?.data?.details ?? 'FetchKO'
      });
      return Promise.reject(e.data ? e.data : e);
    }
  }
);

export const linkJobAsync = createAsyncThunk(
  'monitoring/linkJob',
  async ({
    ref,
    job_id,
    droneIdentifier,
    ctx,
    job_label,
    radius,
    height,
    isResume,
    communicationId
  }: {
    ref: any;
    job_id: any;
    droneIdentifier : any;
    ctx: any;
    job_label: any;
    radius: any;
    height: any;
    isResume: boolean;
    communicationId: any;
  }) => {
    try {
      console.log('communicationId');
      console.log(communicationId);
      const response: any = await linkJob(ref, job_id, droneIdentifier, ctx,
        job_label, radius, height, isResume, communicationId);
      return response.data;
    } catch (e: any) {
      openToast({
        severity: 'error',
        alertMessage: e?.data?.details ?? 'FetchKO'
      });
      return Promise.reject(e.data ? e.data : e);
    }
  }
);

export const unlinkJobAsync = createAsyncThunk(
  'monitoring/unlinkJob',
  async ({ ref, job_id, ctx }: { ref: any; job_id: any; ctx: any }) => {
    try {
      const response: any = await unlinkJob(ref, job_id, ctx);
      return response.data;
    } catch (e: any) {
      openToast({
        severity: 'error',
        alertMessage: e?.data?.details ?? 'FetchKO'
      });
      return Promise.reject(e.data ? e.data : e);
    }
  }
);

export const checkJobAsync = createAsyncThunk(
  'monitoring/checkJob',
  async ({ ref, job_id, ctx }: { ref: any; job_id: any; ctx: any }) => {
    try {
      const response: any = await checkJob(ref, job_id, ctx);
      return response.data;
    } catch (e: any) {
      return Promise.reject(e.data ? e.data : e);
    }
  }
);

export const checkSocketConnected = createAsyncThunk(
  'monitoring/checkSocketConnected',
  async ({ uavId, username }: { uavId: string; username: string }) => {
    try {
      const response: any = await fetchSocketConnected(uavId, username);
      return response.data;
    } catch (e: any) {
      return Promise.reject(e.data ? e.data : e);
    }
  }
);

export const monitoringSlice = createSlice({
  name: 'monitoring',
  initialState,
  reducers: {
    resetMonitoringFlights: (state) => {
      state.flights.status = 'idle';
      state.flights.data = null;
    },
    setMonitoringDetail: (state) => {
      state.detail = true;
    },
    resetMonitoringDetail: (state) => {
      state.detail = false;
    },
    setMonitoringDroneDetail: (state, action) => {
      state.droneDetail.status = 'success';
      state.droneDetail.data = action.payload;
    },
    resetMonitoringDroneDetail: (state) => {
      state.droneDetail.status = 'idle';
      state.droneDetail.data = null;
    },

    resetMonitoringDronePopup: (state) => {
      state.dronePopup.status = 'idle';
      state.dronePopup.data = null;
    },
    resetMonitoringDrone: (state) => {
      state.drone.status = 'idle';
      state.drone.data = null;
    },
    resetMonitoringDrones: (state) => {
      state.dronesAll.status = 'idle';
      state.dronesAll.data = [];
    },
    resetMonitoringHangar: (state) => {
      state.hangar.status = 'idle';
      state.hangar.data = null;
    },
    resetMonitoringHangarControl: (state) => {
      state.hangarControl.status = 'idle';
      state.hangarControl.data = null;
    },
    resetCleosList: (state) => {
      state.cleosList.status = 'idle';
      state.cleosList.data = null;
    },
    resetColorDetectionsList: (state) => {
      state.colorDetectionsList.status = 'idle';
      state.colorDetectionsList.data = null;
    },
    resetCleosStatus: (state) => {
      state.cleosStatus.status = 'idle';
      state.cleosStatus.data = undefined;
    },

    setCleosTimer: (state,  action) => {
      state.cleosTimer = action.payload;
    },
    setC2Timer: (state,  action) => {
      state.c2Timer = action.payload;
    },



    setCleosRunId: (state,  action) => {
      state.cleosRunId = action.payload;
    },


    setGeoState: (state, action) => {
      state.geoState = action.payload;
    },
    resetGeoState: (state ) => {
      state.geoState = {};
    },
    setDetectionActionState: (state, action) => {
      state.detectionActionState = action.payload;
    },
    resetDetectionActionState: (state ) => {
      state.detectionActionState = {};
    },
    resetStartCleosStream: (state) => {
      state.startCleosStream.status = 'idle';
      state.startCleosStream.data = null;
    },
    resetDroneState: (state) => {
      state.droneState.status = 'idle';
      state.droneState.data = null;
    },
    resetDroneControl: (state) => {
      state.droneControl.status = 'idle';
      state.droneControl.data = null;
    },
    setTrackingMode: (state, action) => {
      state.trackingMode = action.payload;
    },
    resetTrackingMode: (state) => {
      state.trackingMode = '';
    },
    resetCleosActiveStreams: (state) => {
      state.cleosActiveStreams.status = 'idle';
      state.cleosActiveStreams.data = null;
    },
    resetMavlink: (state) => {
      state.mavlink.status = 'idle';
      state.mavlink.data = null;
    },
    setDetectionGeoLocation: (state, action) => {

      if (state.detectionGeoLocation.which_detection.indexOf(action)!=-1){
        state.detectionGeoLocation.which_detection =
          state.detectionGeoLocation.which_detection.filter(it => it!=action);
      }
      else
      {
        state.detectionGeoLocation.which_detection.push(action);
      }
    },
    resetDetectionGeoLocation: (state) => {
      state.detectionGeoLocationFetch.status = 'idle';
      state.detectionGeoLocationFetch.data = [];
    },
    resetDetectionGeoLocationGeo: (state) => {
      state.detectionGeoLocation.which_detection = [];
    },

    setGuided: (state, action) => {
      state.guided = action.payload;
    },
    resetGuided: (state) => {
      state.guided = '';
    },
    setGuidedFlag: (state, action) => {
      state.guidedFlag = action.payload;
    },
    resetGuidedFlag: (state, action) => {
      state.guidedFlag = action.payload;
    },
    resetSocketConnected: (state) => {
      state.socketConnected.status = 'idle';
      state.socketConnected.data = false;
    },
    setValueTab: (state, action) => {
      state.value = action.payload;
    },
    resetValueTab: (state) => {
      state.value = 0;
    },
    setZoom: (state, action) => {
      state.zoom = action.payload;
    },
    resetZoom: (state) => {
      state.zoom = 7;
    },
    setClickOnMap: (state, action) => {
      state.featuresClick = action.payload;
    },
    resetClickOnMap: (state) => {
      state.featuresClick = null;
    },
    resetMAVLinkInitialized: (state) => {
      state.mavLinkInitialized.status = 'idle';
      state.mavLinkInitialized.data = false;
    },
    setInputBuffer: (state, action) => {
      state.inputBuffer.status = 'success';
      state.inputBuffer.data = action.payload;
    },
    resetInputBuffer: (state) => {
      state.inputBuffer.status = 'idle';
      state.inputBuffer.data = { distance: 1, height: 15, quotaMax: 0, distanceV: 1 };
    },
    resetCheckTaskData: (state) => {
      state.checkTaskData.status = 'idle';
      state.checkTaskData.data = null;
    },
    resetCheckJobData: (state) => {
      state.checkJobData.status = 'idle';
      state.checkJobData.data = null;
    },
    resetLinkTaskData: (state) => {
      state.linkTaskData.status = 'idle';
      state.linkTaskData.data = null;
    },
    resetUnlinkTaskData: (state) => {
      state.unlinkTaskData.status = 'idle';
      state.unlinkTaskData.data = null;
    },
    resetLinkJobData: (state) => {
      state.linkJobData.status = 'idle';
      state.linkJobData.data = null;
    },
    resetUnlinkJobData: (state) => {
      state.unlinkJobData.status = 'idle';
      state.unlinkJobData.data = null;
    },
    setSocketInitialized: (state, action) => {
      state.socketInitialized = action.payload;
    },
    setSelected: (state, action) => {
      state.selected = action.payload;
    },
    resetSelected: (state) => {
      state.selected = false;
    },
    setAutomaticActionEnabled: (state, action) => {
      state.automaticActionEnabled = action.payload;
    },
    resetAutomaticActionEnabled: (state) => {
      state.automaticActionEnabled = false;
    },
    setPopupView: (state, action) => {
      state.popupView = action.payload;
    },
    resetPopupView: (state) => {
      state.popupView = null;
    },
    resetSettingMission: (state) => {
      state.changeIconState.status = 'idle';
      state.changeCylinderState.status = 'idle';
      state.icons.status = 'idle';
    },

  },
  extraReducers: (builder) => {
    builder
      .addCase(getFlightsAsync.pending, (state) => {
        state.flights.status = 'loading';
      })
      .addCase(getFlightsAsync.fulfilled, (state, action) => {
        state.flights.status = 'success';
        state.flights.data = action.payload;
      })
      .addCase(getFlightsAsync.rejected, (state: any, action) => {
        state.flights.status = 'failed';
        state.flights.data = action.error;
      })
      .addCase(getDronePositionAsync.pending, (state) => {
        state.drone.status = 'loading';
      })
      .addCase(getDronePositionAsync.fulfilled, (state, action) => {
        state.drone.status = 'success';
        state.drone.data = action.payload;
      })
      .addCase(getDronePositionAsync.rejected, (state: any, action) => {
        state.drone.status = 'failed';
        state.drone.data = action.error;
      })
      .addCase(getDronesPositionAsync.pending, (state) => {
        state.drones.status = 'loading';
      })
      .addCase(getDronesPositionAsync.fulfilled, (state, action) => {
        state.drones.status = 'success';
        state.drones.data = action.payload;
      })
      .addCase(getDronesPositionAsync.rejected, (state: any, action) => {
        state.drones.status = 'failed';
        state.drones.data = action.error;
      })
      .addCase(getDronesPositionAsyncAll.pending, (state) => {
        state.dronesAll.status = 'loading';
      })
      .addCase(getDronesPositionAsyncAll.fulfilled, (state, action) => {
        state.dronesAll.status = 'success';
        state.dronesAll.data = action.payload;
      })
      .addCase(getDronesPositionAsyncAll.rejected, (state: any, action) => {
        state.dronesAll.status = 'failed';
        state.dronesAll.data = action.error;
      })
      .addCase(getDronesConflictAsync.pending, (state) => {
        state.dronesConflict.status = 'loading';
      })
      .addCase(getDronesConflictAsync.fulfilled, (state, action) => {
        state.dronesConflict.status = 'success';
        state.dronesConflict.data = action.payload;
      })
      .addCase(getDronesConflictAsync.rejected, (state: any, action) => {
        state.dronesConflict.status = 'failed';
        state.dronesConflict.data = action.error;
      })
      .addCase(getHangarByHIDAsync.pending, (state) => {
        state.hangar.status = 'loading';
      })
      .addCase(getHangarByHIDAsync.fulfilled, (state, action) => {
        state.hangar.status = 'success';
        state.hangar.data = action.payload;
      })
      .addCase(getHangarByHIDAsync.rejected, (state: any, action) => {
        state.hangar.status = 'failed';
        state.hangar.data = action.error;
      })
      .addCase(getHangarAsync.pending, (state) => {
        state.hangar.status = 'loading';
      })
      .addCase(getHangarAsync.fulfilled, (state, action) => {
        state.hangar.status = 'success';
        state.hangar.data = action.payload;
      })
      .addCase(getHangarAsync.rejected, (state: any, action) => {
        state.hangar.status = 'failed';
        state.hangar.data = action.error;
      })
      .addCase(postHangarAsync.pending, (state) => {
        state.hangarControl.status = 'loading';
      })
      .addCase(postHangarAsync.fulfilled, (state, action) => {
        state.hangarControl.status = 'success';
        state.hangarControl.data = action.payload;
      })
      .addCase(postHangarAsync.rejected, (state: any, action) => {
        state.hangarControl.status = 'failed';
        state.hangarControl.data = action.error;
      })
      .addCase(getCleosListAsync.pending, (state) => {
        state.cleosList.status = 'loading';
      })
      .addCase(getCleosListAsync.fulfilled, (state, action) => {
        state.cleosList.status = 'success';
        state.cleosList.data = action.payload;
      })
      .addCase(getCleosListAsync.rejected, (state: any, action) => {
        state.cleosList.status = 'failed';
        state.cleosList.data = action.error;
      })
      .addCase(getColorDetectionsListAsync.pending, (state) => {
        state.colorDetectionsList.status = 'loading';
      })
      .addCase(getColorDetectionsListAsync.fulfilled, (state, action) => {
        state.colorDetectionsList.status = 'success';
        state.colorDetectionsList.data = action.payload;
      })
      .addCase(getColorDetectionsListAsync.rejected, (state: any, action) => {
        state.colorDetectionsList.status = 'failed';
        state.colorDetectionsList.data = action.error;
      })
      .addCase(getCleosStatusAsync.pending, (state) => {
        state.cleosStatus.status = 'loading';
      })
      .addCase(getCleosStatusAsync.fulfilled, (state, action) => {
        state.cleosStatus.status = 'success';
        state.cleosStatus.data = action.payload;
      })
      .addCase(getCleosStatusAsync.rejected, (state: any, action) => {
        state.cleosStatus.status = 'failed';
        state.cleosStatus.data = action.error;
      })
      .addCase(startCleosStreamAsync.pending, (state) => {
        state.startCleosStream.status = 'loading';
      })
      .addCase(startCleosStreamAsync.fulfilled, (state, action) => {
        state.startCleosStream.status = 'success';
        state.startCleosStream.data = { ...action.payload, ...action.meta.arg };
      })
      .addCase(startCleosStreamAsync.rejected, (state: any, action) => {
        state.startCleosStream.status = 'failed';
        state.startCleosStream.data = action.error;
      })
      .addCase(getDroneStateAsync.pending, (state) => {
        state.droneState.status = 'loading';
      })
      .addCase(getDroneStateAsync.fulfilled, (state, action) => {
        state.droneState.status = 'success';
        state.droneState.data = action.payload;
      })
      .addCase(getDroneStateAsync.rejected, (state: any, action) => {
        state.droneState.status = 'failed';
        state.droneState.data = action.error;
      })
      .addCase(postDroneControlAsync.pending, (state) => {
        state.droneControl.status = 'loading';
      })
      .addCase(postDroneControlAsync.fulfilled, (state, action) => {
        state.droneControl.status = 'success';
        state.droneControl.data = action.payload;
      })
      .addCase(postDroneControlAsync.rejected, (state: any, action) => {
        state.droneControl.status = 'failed';
        state.droneControl.data = action.error;
      })
      .addCase(getCleosActiveStreamsAsync.pending, (state) => {
        state.cleosActiveStreams.status = 'loading';
      })
      .addCase(getCleosActiveStreamsAsync.fulfilled, (state, action) => {
        state.cleosActiveStreams.status = 'success';
        state.cleosActiveStreams.data = action.payload;
      })
      .addCase(getCleosActiveStreamsAsync.rejected, (state: any, action) => {
        state.cleosActiveStreams.status = 'failed';
        state.cleosActiveStreams.data = action.error;
      })
      .addCase(getMavlinkControlAsync.pending, (state) => {
        state.mavlink.status = 'loading';
      })
      .addCase(getMavlinkControlAsync.fulfilled, (state, action) => {
        state.mavlink.status = 'success';
        state.mavlink.data = action.payload;
      })
      .addCase(getMavlinkControlAsync.rejected, (state: any, action) => {
        state.mavlink.status = 'failed';
        state.mavlink.data = action.error;
      })
      .addCase(postMavlinkControlAsync.pending, (state) => {
        state.mavlinkLoadMission.status = 'loading';
      })
      .addCase(postMavlinkControlAsync.fulfilled, (state, action) => {
        state.mavlinkLoadMission.status = 'success';
        state.mavlinkLoadMission.data = action.payload;
      })
      .addCase(postMavlinkControlAsync.rejected, (state: any, action) => {
        state.mavlinkLoadMission.status = 'failed';
        state.mavlinkLoadMission.data = action.error;
      })
      .addCase(getDetectionGeolocationAsync.pending, (state) => {
        state.detectionGeoLocationFetch.status = 'loading';
      })
      .addCase(getDetectionGeolocationAsync.fulfilled, (state, action) => {
        state.detectionGeoLocationFetch.status = 'success';
        state.detectionGeoLocationFetch.data = action.payload;
      })
      .addCase(getDetectionGeolocationAsync.rejected, (state: any, action) => {
        state.detectionGeoLocationFetch.status = 'failed';
        state.detectionGeoLocationFetch.data = action.error;
      })
      .addCase(getMavlinkInitializedAsync.pending, (state) => {
        state.mavLinkInitialized.status = 'loading';
      })
      .addCase(getMavlinkInitializedAsync.fulfilled, (state, action) => {
        state.mavLinkInitialized.status = 'success';
        state.mavLinkInitialized.data = action.payload;
      })
      .addCase(getMavlinkInitializedAsync.rejected, (state: any, action) => {
        state.mavLinkInitialized.status = 'failed';
        state.mavLinkInitialized.data = action.error;
      })
      .addCase(checkTaskAsync.pending, (state) => {
        state.checkTaskData.status = 'loading';
      })
      .addCase(checkTaskAsync.fulfilled, (state, action) => {
        state.checkTaskData.status = 'success';
        state.checkTaskData.data = action.payload;
      })
      .addCase(checkTaskAsync.rejected, (state: any, action) => {
        state.checkTaskData.status = 'failed';
        state.checkTaskData.data = action.error;
      })
      .addCase(linkJobAsync.pending, (state) => {
        state.linkJobData.status = 'loading';
      })
      .addCase(linkJobAsync.fulfilled, (state, action) => {
        state.linkJobData.status = 'success';
        state.linkJobData.data = action.payload;
      })
      .addCase(linkJobAsync.rejected, (state: any, action) => {
        state.linkJobData.status = 'failed';
        state.linkJobData.data = action.error;
      })
      .addCase(unlinkJobAsync.pending, (state) => {
        state.unlinkJobData.status = 'loading';
      })
      .addCase(unlinkJobAsync.fulfilled, (state, action) => {
        state.unlinkJobData.status = 'success';
        state.unlinkJobData.data = action.payload;
      })
      .addCase(unlinkJobAsync.rejected, (state: any, action) => {
        state.unlinkJobData.status = 'failed';
        state.unlinkJobData.data = action.error;
      })
      .addCase(unlinkTaskAsync.pending, (state) => {
        state.unlinkTaskData.status = 'loading';
      })
      .addCase(unlinkTaskAsync.fulfilled, (state, action) => {
        state.unlinkTaskData.status = 'success';
        state.unlinkTaskData.data = action.payload;
      })
      .addCase(unlinkTaskAsync.rejected, (state: any, action) => {
        state.unlinkTaskData.status = 'failed';
        state.unlinkTaskData.data = action.error;
      })
      .addCase(linkTaskAsync.pending, (state) => {
        state.linkTaskData.status = 'loading';
      })
      .addCase(linkTaskAsync.fulfilled, (state, action) => {
        state.linkTaskData.status = 'success';
        state.linkTaskData.data = action.payload;
      })
      .addCase(linkTaskAsync.rejected, (state: any, action) => {
        state.linkTaskData.status = 'failed';
        state.linkTaskData.data = action.error;
      })
      .addCase(checkJobAsync.pending, (state) => {
        state.checkJobData.status = 'loading';
      })
      .addCase(checkJobAsync.fulfilled, (state, action) => {
        state.checkJobData.status = 'success';
        state.checkJobData.data = action.payload;
      })
      .addCase(checkJobAsync.rejected, (state: any, action) => {
        state.checkJobData.status = 'failed';
        state.checkJobData.data = action.error;
      })
      .addCase(checkSocketConnected.pending, (state) => {
        state.socketConnected.status = 'loading';
      })
      .addCase(checkSocketConnected.fulfilled, (state, action) => {
        state.socketConnected.status = 'success';
        state.socketConnected.data = action.payload;
      })
      .addCase(checkSocketConnected.rejected, (state: any, action) => {
        state.socketConnected.status = 'failed';
        state.socketConnected.data = action.error;
      })
      .addCase(changeIconToUavId.pending, (state) => {
        state.changeIconState.status = 'loading';
      })
      .addCase(changeIconToUavId.fulfilled, (state, action) => {
        state.changeIconState.status = 'success';
      })
      .addCase(changeIconToUavId.rejected, (state: any, action) => {
        state.changeIconState.status = 'failed';
      })
      .addCase(changeCylinderToUavId.pending, (state) => {
        state.changeCylinderState.status = 'loading';
      })
      .addCase(changeCylinderToUavId.fulfilled, (state, action) => {
        state.changeCylinderState.status = 'success';
      })
      .addCase(changeCylinderToUavId.rejected, (state: any, action) => {
        state.changeCylinderState.status = 'failed';
      })

      .addCase(getIcons.fulfilled, (state, action) => {
        state.icons.status = 'success';
        state.icons.data = action.payload;
      })
      .addCase(getIcons.rejected, (state: any, action) => {
        state.icons.status = 'failed';
      })
      .addCase(getIcons.pending, (state: any, action) => {
        state.icons.status = 'loading';
      });



  }
});

export const {
  resetMonitoringFlights,
  setMonitoringDetail,
  resetMonitoringDetail,
  setMonitoringDroneDetail,
  resetMonitoringDroneDetail,
  resetMonitoringDronePopup,
  resetMonitoringDrone,
  resetMonitoringDrones,
  resetMonitoringHangar,
  resetMonitoringHangarControl,
  resetCleosList,
  resetColorDetectionsList,
  setGeoState,
  setDetectionActionState,
  resetCleosStatus,
  setCleosTimer,
  setC2Timer,
  setCleosRunId,
  resetStartCleosStream,
  resetGeoState,
  resetDetectionActionState,
  resetDroneState,
  resetDroneControl,
  setTrackingMode,
  resetTrackingMode,
  resetCleosActiveStreams,
  resetMavlink,
  setDetectionGeoLocation,
  resetDetectionGeoLocation,
  resetDetectionGeoLocationGeo,
  setGuided,
  resetGuided,
  setValueTab,
  resetValueTab,
  setZoom,
  resetZoom,
  setClickOnMap,
  resetClickOnMap,
  resetMAVLinkInitialized,
  setInputBuffer,
  resetInputBuffer,
  resetCheckTaskData,
  resetCheckJobData,
  resetLinkTaskData,
  resetUnlinkTaskData,
  resetLinkJobData,
  resetUnlinkJobData,
  setGuidedFlag,
  resetGuidedFlag,
  resetSocketConnected,
  setSocketInitialized,
  setSelected,
  resetSelected,
  setAutomaticActionEnabled,
  resetAutomaticActionEnabled,
  resetSettingMission,
  setPopupView,
  resetPopupView
} = monitoringSlice.actions;
export const monitoringFlightsState = (state: RootState) =>
  state.monitoring.flights;
export const monitoringDetailState = (state: RootState) =>
  state.monitoring.detail;
export const monitoringDroneDetailState = (state: RootState) =>
  state.monitoring.droneDetail;
export const monitoringDroneState = (state: RootState) =>
  state.monitoring.drone;
export const monitoringDronesState = (state: RootState) =>
  state.monitoring.drones;
export const monitoringDronesStateAll = (state: RootState) =>
  state.monitoring.dronesAll;
export const monitoringDronesConflict = (state: RootState) =>
  state.monitoring.dronesConflict;
export const monitoringHangarState = (state: RootState) =>
  state.monitoring.hangar;
export const monitoringHangarControlState = (state: RootState) =>
  state.monitoring.hangarControl;
export const cleosListState = (state: RootState) => state.monitoring.cleosList;
export const colorDetectionsListState = (state: RootState) => state.monitoring.colorDetectionsList;
export const cleosState = (state: RootState) => state.monitoring.cleosStatus;
export const cleosTimerState = (state: RootState) => state.monitoring.cleosTimer;
export const c2TimerState = (state: RootState) => state.monitoring.c2Timer;
export const cleosRunIdState = (state: RootState) => state.monitoring.cleosRunId;
export const cleosStartStreamState = (state: RootState) =>
  state.monitoring.startCleosStream;
export const droneState = (state: RootState) => state.monitoring.droneState;
export const droneControlState = (state: RootState) =>
  state.monitoring.droneControl;
export const trackingModeState = (state: RootState) =>
  state.monitoring.trackingMode;
export const cleosActiveStreamsState = (state: RootState) =>
  state.monitoring.cleosActiveStreams;
export const mavlinkState = (state: RootState) => state.monitoring.mavlink;
export const mavlinkLoadMission = (state: RootState) => state.monitoring.mavlinkLoadMission;

export const detectionLocationState = (state: RootState) =>
  state.monitoring.detectionGeoLocation;
export const detectionLocationFetch = (state: RootState) =>
  state.monitoring.detectionGeoLocationFetch;
export const guidedState = (state: RootState) => state.monitoring.guided;
export const guidedFlagState = (state: RootState) =>
  state.monitoring.guidedFlag;
export const socketConnectedState = (state: RootState) =>
  state.monitoring.socketConnected;
export const valueState = (state: RootState) => state.monitoring.value;
export const zoomState = (state: RootState) => state.monitoring.zoom;
export const featuresClickState = (state: RootState) => state.monitoring.featuresClick;
export const mavLinkInitializedState = (state: RootState) =>
  state.monitoring.mavLinkInitialized;
export const inputBufferState = (state: RootState) =>
  state.monitoring.inputBuffer;
export const checkTaskState = (state: RootState) =>
  state.monitoring.checkTaskData;
export const linkJobState = (state: RootState) => state.monitoring.linkJobData;
//export const lastPosJobState = (state: RootState) => state.monitoring.lastPosJobData;
export const unlinkJobState = (state: RootState) =>
  state.monitoring.unlinkJobData;
export const unlinkTaskState = (state: RootState) =>
  state.monitoring.unlinkTaskData;
export const linkTaskState = (state: RootState) =>
  state.monitoring.linkTaskData;
export const checkJobState = (state: RootState) =>
  state.monitoring.checkJobData;
export const socketInitializedState = (state: RootState) =>
  state.monitoring.socketInitialized;
export const selectedState = (state: RootState) => state.monitoring.selected;

export const detectionGeoState = (state: RootState) => state.monitoring.geoState;
export const detectionActionState = (state: RootState) => state.monitoring.detectionActionState;
export const automaticActionEnabledState = (state: RootState) =>
  state.monitoring.automaticActionEnabled;
export const changeIconState = (state: RootState) =>
  state.monitoring.changeIconState;
export const changeCylinderState = (state: RootState) =>
  state.monitoring.changeCylinderState;
export const monitoringPopupView = (state: RootState) =>
  state.monitoring.popupView;
export const monitoringIconsState = (state: RootState) =>
  state.monitoring.icons;
export default monitoringSlice.reducer;
